// src/pages/Public/TrackingPage.jsx
// GOV-7 WS2-B / Phase 2 — PUBLIC, UNAUTHENTICATED customer tracking page. The ONLY new surface in this workstream.
//
// This is NOT Customer Web: no login, no account nav, no bookings list, no business/job/customer selector,
// no query-by-id. The ONLY input is the opaque token in the URL. It fetches GET /api/v1/public/tracking/{token}
// and renders ONLY customer-safe fields returned by the backend: business display NAME, human status label,
// pickup/destination labels, truthful ETA, remaining distance, last-updated, and a visual Google Map of the
// backend-projected route + current mover + pickup/destination markers, plus stale / ended states.
// Bad / expired / revoked token → a truthful "tracking unavailable" message that does NOT leak whether other
// records exist. NO internal ids are ever rendered.
//
// Phase 2 additions (Web-only; NO backend/CSP change): TrackingMap (Google Maps = renderer only; server route
// polyline; no client Routes/Directions), a single controlled poll (the public GET only READS the persisted
// projection — it never triggers a provider/ETA recompute, so a refresh creates NO google_routes_compute meter),
// and a remaining-distance line.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import TrackingMap from './TrackingMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_MS = 20000; // ONE controlled refresh cadence; reuses the SAME read-only public GET (never a recompute).

function fmtTs(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

// Remaining distance from the backend projection (meters) → human label. Never client-computed from coords.
function fmtDistance(m) {
  if (m == null || Number.isNaN(Number(m))) return null;
  const meters = Number(m);
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

// Truthful ETA rendering from the backend eta_state — never client-computed, never a fake "0 min".
function EtaLine({ data }) {
  const state = data.eta_state;
  const lastUpdated = fmtTs(data.last_updated_at);
  if (state === 'available' && data.duration_seconds != null) {
    const mins = Math.round(data.duration_seconds / 60);
    const arr = fmtTs(data.estimated_arrival_at);
    return (
      <p className="text-lg" data-testid="track-eta-available">
        Arriving in about <b>{mins} min{mins === 1 ? '' : 's'}</b>
        {arr ? <span className="text-gray-500 text-sm"> · est. {arr}</span> : null}
      </p>
    );
  }
  if (state === 'stale') {
    return (
      <p className="text-gray-600" data-testid="track-eta-stale">
        ETA is temporarily out of date{lastUpdated ? ` (last updated ${lastUpdated})` : ''}.
      </p>
    );
  }
  if (state === 'no_data') {
    return <p className="text-gray-600" data-testid="track-eta-nodata">An ETA isn’t available yet.</p>;
  }
  return <p className="text-gray-600" data-testid="track-eta-unavailable">A live ETA isn’t available right now.</p>;
}

const TrackingPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, unavailable: false });

  useEffect(() => {
    let alive = true;
    let timer = null;
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

    const fetchOnce = async (isInitial) => {
      if (!token) { if (alive) setState({ loading: false, data: null, unavailable: true }); return; }
      try {
        // UNAUTH — no Authorization header. Opaque token only. Read-only projection (no recompute → no meter).
        const r = await fetch(`${BACKEND_URL}/api/v1/public/tracking/${encodeURIComponent(token)}`);
        if (!alive) return;
        if (!r.ok) { setState({ loading: false, data: null, unavailable: true }); stop(); return; } // 404/expired/revoked
        const data = await r.json();
        if (!alive) return;
        setState({ loading: false, data, unavailable: false });
        if (data && data.tracking_ended === true) stop(); // stop polling once the job is complete
      } catch (e) {
        // Only the FIRST load surfaces "unavailable"; a transient poll error keeps the last good data + polling.
        if (alive && isInitial) setState({ loading: false, data: null, unavailable: true });
      }
    };

    fetchOnce(true);
    timer = setInterval(() => fetchOnce(false), POLL_MS); // exactly ONE timer for the lifetime of the page
    return () => { alive = false; stop(); };
  }, [token]);

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-white via-purple-50 to-white" data-testid="tracking-page">
      <header className="bg-gradient-to-r from-purple-700 to-purple-900 text-white py-5 px-6 shadow-md">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-xl font-bold tracking-tight">Live Tracking</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-10">{children}</main>
    </div>
  );

  if (state.loading) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="track-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading tracking…</p>
        </div>
      </Shell>
    );
  }

  // Bad / expired / revoked / missing token — IDENTICAL message regardless of cause (no record-existence leak).
  //
  // The commonest way a customer reaches this branch is the happy path: their job finished. The tracking token
  // is revoked the moment the job completes, so the backend 404s and the `tracking_ended` branch below can
  // never be reached post-completion. The old heading ("Tracking unavailable") therefore told customers with a
  // successfully COMPLETED job that something was broken.
  //
  // We deliberately do NOT say "this job is complete" here: a 404 cannot distinguish a finished job from a
  // bogus token, so asserting completion would be untrue for an invalid link AND would turn this page into an
  // existence oracle. Instead the copy is truthful for BOTH causes and explains the normal reason, without
  // implying a fault. A definitive completed state would require a token-lifecycle change (owner decision).
  if (state.unavailable || !state.data) {
    return (
      <Shell>
        <div className="text-center py-16" data-testid="track-unavailable">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">This tracking link is no longer active</h2>
          <p className="text-gray-600">Live tracking ends once the job is complete.</p>
          <p className="text-gray-500 text-sm mt-2">If you need an update, please contact the business directly.</p>
        </div>
      </Shell>
    );
  }

  const d = state.data;
  const wf = d.workflow_state || {};
  const statusLabel = wf.label || wf.status_label || null;   // human label only; never the raw status enum
  const ended = d.tracking_ended === true;
  const pos = d.current_position && d.current_position.lat != null && d.current_position.lng != null
    ? d.current_position : null;
  const lastUpdated = fmtTs(d.last_updated_at);

  return (
    <Shell>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-400">Service by</p>
          <h2 className="text-2xl font-bold text-gray-900" data-testid="track-business">{d.business_display_name || 'Your provider'}</h2>
        </div>

        {statusLabel && (
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-400">Status</p>
            <p className="text-lg font-medium text-purple-700" data-testid="track-status">{statusLabel}</p>
          </div>
        )}

        {ended ? (
          <div className="rounded-lg bg-gray-50 p-4 text-center" data-testid="track-ended">
            <p className="text-gray-700 font-medium">Tracking has ended</p>
            <p className="text-gray-500 text-sm">This job is complete. Thanks for using {d.business_display_name || 'our service'}.</p>
          </div>
        ) : (
          <>
            {/* Visual map: Google Maps renderer of the backend-projected route + markers. No client routing. */}
            <TrackingMap data={d} />
            <div className="rounded-lg bg-purple-50 p-4">
              <EtaLine data={d} />
              {fmtDistance(d.remaining_distance_meters) && (
                <p className="text-sm text-gray-600 mt-1" data-testid="track-distance">
                  {fmtDistance(d.remaining_distance_meters)} to go
                </p>
              )}
              {lastUpdated && <p className="text-xs text-gray-400 mt-1" data-testid="track-updated">Last updated {lastUpdated}</p>}
            </div>
          </>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {d.pickup_address_label && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">Pickup</p>
              <p className="text-gray-800" data-testid="track-pickup">{d.pickup_address_label}</p>
            </div>
          )}
          {d.destination_address_label && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-400">Destination</p>
              <p className="text-gray-800" data-testid="track-destination">{d.destination_address_label}</p>
            </div>
          )}
        </div>

        {/* Read-only position — customer-safe coordinates only. NO Google JS SDK, NO credentials, NO map keys. */}
        {!ended && pos && (
          <div className="text-xs text-gray-500" data-testid="track-position">
            Current location: {Number(pos.lat).toFixed(4)}, {Number(pos.lng).toFixed(4)}
          </div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-6">Powered by Servisfy</p>
    </Shell>
  );
};

export default TrackingPage;
